import React, { useEffect, useState } from 'react';
import { AuthContext } from '../../../components/AuthWrapper';
import { useOutletContext } from 'react-router-dom';
import { Campus, Semester, StudentsFilter, TableStudent } from '../../../api/client';
import SelectComponentModal from '../../../components/SelectItemComponent';
import { Button } from "../../../components/UiComponents/button";
import {
  ColumnFiltersState,
  getFilteredRowModel,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/UiComponents/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../components/UiComponents/select";
import { DataTableProps, defaultFilter, searchableFields } from './Helpers';
import { Heading } from '../../../components/UiComponents/heading';
import { Input } from '../../../components/Input';
import { GetLocalFrontendBaseUrl } from '../../../helpers/stringHelpers';
import { fetchDWStudentProfileSyncTimestamp, processDWSynchTimestampToFrontendView } from '../Admin/AdminComponents';
import { ArrowRepeat } from 'react-bootstrap-icons';
import toast from 'react-hot-toast';

export function DataTable<TData, TValue>({
  columns,
}: DataTableProps<TData, TValue>) {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [selectedStudentTypes, setSelectedStudentTypes] = useState<string[]>([]);
  const [selectedPersonalInterests, setSelectedPersonalInterests] = useState<string[]>([]);
  const [selectedProfessionalInterests, setSelectedProfessionalInterests] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedLearningLanguages, setSelectedLearningLanguages] = useState<string[]>([]);
  const { client, staticData }: AuthContext = useOutletContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [data, setData] = useState<TData[]>([]);
  const [allStudentsData, setAllStudentsData] = useState<TableStudent[]>([]);
  const [filters, setFilters] = useState<StudentsFilter>(defaultFilter);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {state }: AuthContext = useOutletContext();

  // Sync With DW Button
  const synchWithDWButtonName: string = "Synchronize Student Profiles from USF DataWarehouse";
  const [dwLastSynchTimestamp, setDWLastSynchTimestamp] = useState<string | null>("");
  const [areUpdatingStudentData, setAreUpdatingStudentData] = useState<boolean>(false);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  const fetchStudents = (filtersForBackend: StudentsFilter) => {
    if (!client) return;
    setLoading(true);
    client.admin_GetStudents(0, 1000, filtersForBackend)
      .then(res => {
        setError(null);
        if (res.results) {
          setAllStudentsData(res.results);
          const mappedStudents = res.results
            .map(student => ({
              studentId: student.studentId,
              userId: student.userId,
              usfId: student.usfId,
              fullName: student.fullName,
              netId: student.netId,
              campus: student.campus || 'Unknown',
              major: student.majors || [],
              expectedGraduationYear: student.expectedGraduationYear ? student.expectedGraduationYear.toString() : 'Unknown',
              expectedGraduationSemester: student.expectedGraduationSemester,
              createdAt: student.createdAt ? new Date(student.createdAt.toString()).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'Unknown',
            }));
          setData(mappedStudents as TData[]);
        } else {
          setData([]);
        }
      })
      .catch((error) => {
        setError(`${error.status}. Failed to load students information. Check your URL.`)
        setData([]);
      });
  };

  useEffect(() => {
    fetchStudents(defaultFilter);

    // Fetch student data from DW button
    const isAdmin: boolean = (!state || !state.user) ? false : (state?.user?.admin !== null && state?.user?.admin !== undefined);
    const fetchTimestamp = fetchDWStudentProfileSyncTimestamp(client, isAdmin, setDWLastSynchTimestamp);
    
    // Only complete loading when both operations are complete
    Promise.all([fetchStudents, fetchTimestamp]).finally(() => {
      setLoading(false);
    });
  }, [state, client]);


   // Separated useEffect to do polling for the timestamp of last Student profile and DW synchronization
  useEffect(() => {
    const isAdmin: boolean = (!state || !state.user) ? false : (state?.user?.admin !== null && state?.user?.admin !== undefined);
    if (!isAdmin) return;

    // Define how often to poll, i.e. every 30 minutes
    const POLL_INTERVAL = 30 * 60 * 1000;

    // The actual interval we need because when someone has the admin page open for a while, some other user could have pressed the button
    // and we need to synchronize it, in the absense of websockets, polling is our only way out
    const intervalId = setInterval(() => {
      fetchDWStudentProfileSyncTimestamp(client, isAdmin, setDWLastSynchTimestamp);
    }, POLL_INTERVAL);

    // This stops the timer when the user leaves the page
    return () => clearInterval(intervalId);
  }, [state, client]);


  const handleAddField = (value: string) => {
    if (!selectedFields.includes(value)) {
      setSelectedFields([...selectedFields, value]);
      setFilters(new StudentsFilter({ ...filters, [value]: '' }));
    }
  };

  const handleRemoveField = (field: string) => {
    setSelectedFields(selectedFields.filter(f => f !== field));
    const updatedFilters = new StudentsFilter(filters);
    delete (updatedFilters as any)[field];
    setFilters(updatedFilters);
    if (field === 'personalInterest') {
      setSelectedPersonalInterests([]);
    } else if (field === 'professionalInterest') {
      setSelectedProfessionalInterests([]);
    } else if (field === 'languagesSpoken') {
      setSelectedLanguages([]);
    } else if (field === 'languagesStudied') {
      setSelectedLearningLanguages([]);
    } else if (field === 'studentType') {
      setSelectedStudentTypes([]);
    }
  };

  const handleFilterChange = (field: string, value: string | number | boolean | string[]) => {
    setFilters(prevFilters => {
      const updatedFilters = new StudentsFilter({ ...prevFilters });
      (updatedFilters as any)[field] = value;
      return updatedFilters;
    });
  };

  const handleSearch = () => {

    let backendFilters = new StudentsFilter(filters);
    for (const filter in backendFilters) {
      // in case campus filter is Tampa put 1 in the filter
      if (filter === 'campus') {
        if ((backendFilters as any)[filter] === 'Tampa') {
          (backendFilters as any)[filter] = 1;
        }
        if ((backendFilters as any)[filter] === 'StPetersburg') {
          (backendFilters as any)[filter] = 2;
        }
        if ((backendFilters as any)[filter] === 'SarasotaManatee') {
          (backendFilters as any)[filter] = 3;
        }
      }

      // For boolean values
      if (Array.isArray((backendFilters as any)[filter])) {
        (backendFilters as any)[filter] = (backendFilters as any)[filter].map((item: string) => item === 'Yes' ? true : item === 'No' ? false : item);
      } else {
        if ((backendFilters as any)[filter] === 'Yes') {
          (backendFilters as any)[filter] = true;
        }
        if ((backendFilters as any)[filter] === 'No') {
          (backendFilters as any)[filter] = false;
        }
      }

      // for expectedGraduationAfter
      if (filter === 'expectedGraduationAfter') {
        let semester = (backendFilters as any)['semester'];
        if (semester === null || semester === undefined) {
          semester = 'Fall';
        }
        let year = (backendFilters as any)[filter];

        if (year === null || year === undefined || year.length === 0) {
          year = '0000';
        }
        (backendFilters as any)[filter] = [semester, year];
      }
    }
    fetchStudents(backendFilters);
  };

  // Helper function to convert campus enum to readable string
  const getCampusName = (campusId: number): string => {
    switch (campusId) {
      case Campus.Tampa: return "Tampa";
      case Campus.StPetersburg: return "St. Petersburg";
      case Campus.SarasotaManatee: return "Sarasota-Manatee";
      default: return "Unknown";
    }
  };

  // Helper function to convert semester enum to readable string
  const getSemesterName = (semesterId: number): string => {
    switch (semesterId) {
      case Semester.Spring: return "Spring";
      case Semester.Summer: return "Summer";
      case Semester.Fall: return "Fall";
      default: return "Unknown";
    }
  };

  // CSV Export Function
  const exportToCSV = (data: any[], filename: string) => {
    // If data is empty, return early
    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    // Custom mapping for headers and data transformation
    const transformedData: { [key: string]: any }[] = data.map(student => ({
      'USF ID': student.usfId,
      'Full Name': student.fullName,
      'Net ID': student.netId,
      'Campus': getCampusName(student.campus),
      'Major': student.major.toString(),
      'Expected Graduation Year': student.expectedGraduationYear,
      'Expected Graduation Semester': getSemesterName(student.expectedGraduationSemester),
      'Created At': student.createdAt
        ? new Date(student.createdAt).toLocaleString()
        : 'N/A'
    }));

    // Extract headers from the first object's keys
    const headers = Object.keys(transformedData[0]);

    // Convert data to CSV format
    const csvContent = [
      headers.join(","), // Header row
      ...transformedData.map(row =>
        headers
          .map(header =>
            // Ensure proper CSV formatting (handling commas, quotes)
            `"${row[header] != null ? String(row[header]).replace(/"/g, '""') : ''}"`
          )
          .join(",")
      )
    ].join("\n");

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a link element to trigger the download
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleRowClick = (usfid: string) => {
    let neededStudent = "placeholder";
    allStudentsData.forEach((student) => {
      if (student.studentId === usfid) {
        neededStudent = student.studentId;
      }
    });
    const newTabUrl = `${GetLocalFrontendBaseUrl()}/admin/studentDetail/${neededStudent}`;
    window.open(newTabUrl, '_blank');
  };

  const handleOpenModal = (field: string) => {
    setSelectedField(field);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedField(null);
  };

  const handleSaveItems = (items: string[]) => {
    if (!selectedField) return;
    setFilters(new StudentsFilter({ ...filters, [selectedField]: items }));

    switch (selectedField) {
      case 'personalInterest':
        setSelectedPersonalInterests(items);
        break;
      case 'professionalInterest':
        setSelectedProfessionalInterests(items);
        break;
      case 'languagesSpoken':
        setSelectedLanguages(items);
        break;
      case 'languagesStudied':
        setSelectedLearningLanguages(items);
        break;
    }

    handleCloseModal();
  };

  const getModalItems = (field: string): string[] => {
    switch (field) {
      case 'personalInterest':
        return staticData?.personalInterests?.map(interest => interest.name) ?? [];
      case 'professionalInterest':
        return staticData?.academicInterests?.map(interest => interest.name) ?? [];
      case 'languagesSpoken':
      case 'languagesStudied':
        return staticData?.languages?.map(language => language.name) ?? [];
      default:
        return [];
    }
  };

  const getSelectedItems = (field: string): string[] => {
    switch (field) {
      case 'personalInterest':
        return selectedPersonalInterests;
      case 'professionalInterest':
        return selectedProfessionalInterests;
      case 'languagesSpoken':
        return selectedLanguages;
      case 'languagesStudied':
        return selectedLearningLanguages;
      default:
        return [];
    }
  };

  const renderFilterInput = (field: string) => {
    const fieldConfig = searchableFields.flatMap(category => category.fields).find(f => f.id === field);

    if (field === 'campus') {
      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex items-center space-x-2 py-2">
            <Select
              // IF value is tampa put 1 in the filter

              onValueChange={(value) => handleFilterChange(field, value)}
              value={(filters as any)[field] || ''}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Campus" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Tampa">Tampa</SelectItem>
                <SelectItem value="StPetersburg">St. Petersburg</SelectItem>
                <SelectItem value="SarasotaManatee">Sarasota-Manatee</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => handleRemoveField(field)}
              variant="destructive"
              size="sm"
              abbr="Remove this filter"
            >
              Remove
            </Button>
          </div>
        </>
      );
    }

    if (field === 'admissionScholarships') {
      const selectedTypes = Array.isArray((filters as any)[field])
        ? (filters as any)[field]
        : [];

      return (
        <>
          <p className='mb-2'>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Select
                onValueChange={(value) => {
                  if (!selectedTypes.includes(value)) {
                    handleFilterChange(
                      field,
                      [...selectedTypes, value]
                    );
                  }
                }}
                value=""
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Add Scholarship" />
                </SelectTrigger>
                <SelectContent>
                  {['Provost scholar', 'Pell Grant Recipient', 'National Merit Scholar'].filter(type => !selectedTypes.includes(type)).map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => handleRemoveField(field)}
                variant="destructive"
                size="sm"
                abbr="Remove this filter"
              >
                Remove
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {selectedTypes.map((type: string) => (
                <div
                  key={type}
                  className="flex items-center gap-1 bg-green-800 text-white pl-4 pr-2 border-1 rounded-full text-sm mb-2 border-white"
                >
                  <span className='bg-transparent'>{type}</span>
                  <Button
                    className='p-2'
                    variant="ghost"
                    onClick={() => {
                      handleFilterChange(
                        field,
                        selectedTypes.filter((t: string) => t !== type)
                      );
                    }}
                    abbr="Deselect this category"
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (field === 'studentType') {
      const selectedTypes = Array.isArray((filters as any)[field])
        ? (filters as any)[field]
        : [];

      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Select
                onValueChange={(value) => {
                  if (!selectedTypes.includes(value)) {
                    handleFilterChange(
                      field,
                      [...selectedTypes, value]
                    );
                  }
                }}
                value=""
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Add Student Type" />
                </SelectTrigger>
                <SelectContent>
                  {['Honors College Student', 'International Student', 'Transfer Student'].filter(type => !selectedTypes.includes(type)).map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => handleRemoveField(field)}
                variant="destructive"
                size="sm"
                abbr="Remove this filter"
              >
                Remove
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {selectedTypes.map((type: string) => (
                <div
                  key={type}
                  className="flex items-center gap-1 bg-green-800 p-2 px-4 border-1 rounded-full text-sm mb-2 border-white"
                >
                  <span className='bg-transparent'>{type}</span>
                  <Button
                    className='p-2'
                    variant="ghost"
                    onClick={() => {
                      handleFilterChange(
                        field,
                        selectedTypes.filter((t: string) => t !== type)
                      );
                    }}
                    abbr="Deselect this category"
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (['personalInterest', 'professionalInterest', 'languagesSpoken', 'languagesStudied'].includes(field)) {
      let parsedField;
      switch(field) {
        case "personalInterest":
          parsedField = "Personal Interest";
          break;
        case "professionalInterest":
          parsedField = "Professional Interest";
          break;
        case "languagesSpoken":
          parsedField = "Languages Spoken";
          break;
        case "languagesStudied":
          parsedField = "Languages Studied";
      }

      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex items-center space-x-2 py-2">
            <Button
              onClick={() => handleOpenModal(field)}
              abbr={`Click to select ${parsedField}`}
            >
              Select {parsedField}
            </Button>
            <span>{getSelectedItems(field).length} selected</span>
            <Button
              onClick={() => handleRemoveField(field)}
              variant="destructive"
              size="sm"
              abbr="Remove this filter"
            >
              Remove
            </Button>
          </div>
        </>
      );
    }
    if (fieldConfig && fieldConfig.category === 'Boolean') {
      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex items-center space-x-2 py-2">
            <Select
              onValueChange={(value) => handleFilterChange(field, value)}
              value={(filters as any)[field] || ''}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={`Filter ${fieldConfig.label}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => handleRemoveField(field)}
              variant="destructive"
              size="sm"
              abbr="Remove this filter"
            >
              Remove
            </Button>
          </div>
        </>
      );
    }

    if (field === 'expectedGraduationAfter') {
      const selectedSemester = (filters as any)['semester'] || '';
      const selectedYear = (filters as any)[field] || '';

      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex items-center space-x-2 py-2">
            <Select
              onValueChange={(value) => handleFilterChange('semester', value)}
              value={selectedSemester}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Semester" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Fall">Fall</SelectItem>
                <SelectItem value="Spring">Spring</SelectItem>
                <SelectItem value="Summer">Summer</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder={`Year`}
              value={selectedYear}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              className="w-[150px]"
            />

            <Button
              onClick={() => handleRemoveField(field)}
              variant="destructive"
              size="sm"
              abbr="Remove this filter"
            >
              Remove
            </Button>
          </div>
        </>
      );
    }

    else {
      return (
        <>
          <p>{fieldConfig ? fieldConfig.label : field}</p>
          <div key={field} className="flex items-center space-x-2 py-2">
            <Input
              placeholder={`Search by ${fieldConfig ? fieldConfig.label : field}`}
              value={(filters as any)[field] || ''}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              className="max-w-sm "
            />
            <Button
              onClick={() => handleRemoveField(field)}
              variant="destructive"
              size="sm"
              abbr="Remove this filter"
            >
              Remove
            </Button>
          </div>
        </>
      );
    }
  };

  return (
    error ? <p>{error}</p> :
      loading ? <p>Loading...</p> :

        <div>

          <Heading text="All Students" />

          {/* Sync Students From DW Button */}
          <div className="flex flex-col items-start justify-center">
            <Button
              variant="default"
              type="button"
              abbr={areUpdatingStudentData ? "Synchronizing student data..." : "Click here to synchronize the students profile data (i.e. gender, GPA) in our database with the USF DataWerehouse."}
              disabled={areUpdatingStudentData}
              onClick={async () => {
                if (!client) return;
                setAreUpdatingStudentData(true);

                // Wrap the call in a promise toast
                await toast.promise(
                  client?.admin_UpdateAllStudentsFromDW(),
                  {
                    loading: 'Updating student profiles...',
                    success: (response) => {
                      setDWLastSynchTimestamp(response.updateTimestamp ? processDWSynchTimestampToFrontendView(response.updateTimestamp) : null);
                      return `${response.updatedStudentsCount} out of ${response.totalStudentsCount} student profiles updated`;
                    },
                    error: 'Failed to update students data',
                  }
                ).finally(() => {
                  setAreUpdatingStudentData(false);
                });
              }}
            >
              <ArrowRepeat className="w-5 h-4" />
              <span>{synchWithDWButtonName}</span>
            </Button>
            <span className="mb-2 text-slate-600 dark:text-slate-300">
              {dwLastSynchTimestamp ? `Last synced: ${dwLastSynchTimestamp}.` : 'No timestamp found.'}
            </span>
          </div>

          <h2>Basic Search</h2>
          <div key={"name"} className="flex items-center space-x-2 py-2">
            <Input
              placeholder={`Filter name`}
              value={(table.getColumn("fullName")?.getFilterValue() as string) ?? ""}
              onChange={(event) => {
                table.getColumn("fullName")?.setFilterValue(event.target.value);
              }}
              className="max-w-sm"
            />
          </div>
          <h2>Advanced Search</h2>
          <div className="flex items-center space-x-2 py-4">
            <Select onValueChange={handleAddField}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Add search field" />
              </SelectTrigger>
              <SelectContent>
                {searchableFields.map((category) => (
                  <SelectGroup key={category.category}>
                    <SelectLabel>{category.category}</SelectLabel>
                    {category.fields.map((field) => (
                      <SelectItem key={field.id} value={field.id}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedFields.map(field => renderFilterInput(field))}
          
          <div className="mb-4 mt-2 flex flex-row gap-2">
            <Button
              variant="default"
              onClick={handleSearch}
              abbr="Click to search with the selected filters"
            >
              Search
            </Button>
            {selectedFields.length > 0 &&
              <Button
                variant="destructive"
                onClick={() => {
                  selectedFields.forEach((field) => {
                    handleRemoveField(field);
                  });
                  fetchStudents(new StudentsFilter());
                }}
                abbr="Reset all filters"
              >
                Reset
              </Button>
            }
          </div> 
          <div className='flex justify-between'>
            <div className="border border-grey rounded p-2 max-w-48  text-center mb-4">
              {data.length} students found
            </div>
            <Button
              variant="default"
              onClick={() => exportToCSV(data, "students")}
              className="ml-4 mt-2"
              abbr="Export and download CSV file"
            >
              Export as CSV
            </Button>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="cursor-pointer"
                      onClick={() => {
                        const studentId = (row.original as TableStudent).studentId;
                        if (studentId) {
                          handleRowClick(studentId);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-end space-x-2 py-4">
            <Button
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              abbr={!table.getCanPreviousPage() ? "Button is disabled" : "View the previous students"}
            >
              Previous
            </Button>
            <Button
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              abbr={!table.getCanNextPage() ? "Button is disabled" : "View the next students"}
            >
              Next
            </Button>
          </div>


          <SelectComponentModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItems}
            allItems={selectedField ? getModalItems(selectedField) : []}
            selectedItems={selectedField ? getSelectedItems(selectedField) : []}
            setSelectedItems={(items) => {
              if (!selectedField) return;
              switch (selectedField) {
                case 'personalInterest':
                  setSelectedPersonalInterests(items);
                  break;
                case 'professionalInterest':
                  setSelectedProfessionalInterests(items);
                  break;
                case 'languagesSpoken':
                  setSelectedLanguages(items);
                  break;
                case 'languagesStudied':
                  setSelectedLearningLanguages(items);
                  break;
              }
            }}
          />
        </div>
  );
}
